import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import GlitchyBackground from '../glitchy-background/glitchy-background.component';
import './homepage.styles.css';

import game1 from '../../assets/game_1.jpg';
import game2 from '../../assets/game_2.jpg';
import game3 from '../../assets/game_3.jpg';
import studyList from '../../assets/study_list.jpg';
import groupsList from '../../assets/groups_list.jpg';
import sentenceWriting from '../../assets/sentence_writing.jpg';
import vocabCards from '../../assets/vocab_cards.jpg';
import cardMaker from '../../assets/card_maker.jpg';
import pronunciation from '../../assets/pronunciation.jpg';

const FEATURES = [
  { title: 'Word Games',       images: [game2, game1, game3],       descriptions: ['Improve your grammar', 'Spell the words as fast as you can', 'Try to win without hints'] },
  { title: 'Study Lists',      images: [studyList, groupsList],     descriptions: ['Study up to 20 words at a time', 'Make your own groups'] },
  { title: 'Sentence Writing', images: [sentenceWriting],           descriptions: ['practice writing your own sentences'] },
  { title: 'Vocab Cards',      images: [vocabCards, cardMaker],     descriptions: ['start with your native language', 'Make your own vocabulary words with AI'] },
  { title: 'Pronunciation',    images: [pronunciation],             descriptions: ['Get instant feedback'] },
];

const TOTAL = FEATURES.length;

const CARD_W  = 160;
const CARD_GAP = 16;
const CARD_STEP = CARD_W + CARD_GAP; // 176
const TRACK_W = CARD_W * 3 + CARD_GAP * 2; // 512

// Absolute left position for each slot
const SLOT = { left: 0, center: CARD_STEP, right: CARD_STEP * 2 };

// Variants use `custom` = { dir, isCenter }
const cardVariants = {
  enter: ({ dir }) => ({ x: dir === 'left' ? 220 : -220, opacity: 0 }),
  visible: ({ isCenter }) => ({ x: 0, opacity: isCenter ? 1 : 0.4, scale: isCenter ? 1 : 0.88 }),
  exit:  ({ dir }) => ({ x: dir === 'left' ? -220 : 220, opacity: 0 }),
};

const CARD_TRANSITION = { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] };

const FeatureCard = ({ title, images, imgIndex, isCenter }) => (
  <div className="lp-feature-card">
    <h3 className="lp-feature-title">{title}</h3>
    {images.length > 0 && (
      <div className="lp-inner-carousel">
        {/* Fixed-height frame keeps the dots put while the image loads or swaps */}
        <div className="lp-inner-img-frame">
          {isCenter ? (
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={`${title}-${imgIndex}`}
                className="lp-feature-img"
                src={images[imgIndex]}
                alt={`${title} screenshot ${imgIndex + 1}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </AnimatePresence>
          ) : (
            <img
              className="lp-feature-img"
              src={images[imgIndex]}
              alt={`${title} screenshot ${imgIndex + 1}`}
            />
          )}
        </div>
        {isCenter && images.length > 1 && (
          <div className="lp-inner-dots">
            {images.map((_, i) => (
              <span
                key={i}
                className={`lp-inner-dot${i === imgIndex ? ' lp-inner-dot-active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
    )}
  </div>
);

const Homepage = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [centerImgIndex, setCenterImgIndex] = useState(0);
  const [featureDir, setFeatureDir] = useState('left');
  const intervalRef = useRef(null);
  const activeIndexRef = useRef(0);
  const centerImgIndexRef = useRef(0);
  // Remembers each feature's last shown image so side cards don't snap to 0 mid-animation
  const featureImgRef = useRef(Object.fromEntries(FEATURES.map(f => [f.title, 0])));

  useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);
  useEffect(() => { centerImgIndexRef.current = centerImgIndex; }, [centerImgIndex]);
  useEffect(() => {
    featureImgRef.current[FEATURES[activeIndex].title] = centerImgIndex;
  }, [activeIndex, centerImgIndex]);

  const currentDesc = useMemo(
    () => FEATURES[activeIndex].descriptions?.[centerImgIndex] || '',
    [activeIndex, centerImgIndex]
  );

  const stopAutoPlay = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const ai = activeIndexRef.current;
      const ci = centerImgIndexRef.current;
      if (ci < FEATURES[ai].images.length - 1) {
        setCenterImgIndex(ci + 1);
      } else {
        setFeatureDir('left');
        setCenterImgIndex(0);
        setActiveIndex((ai + 1) % TOTAL);
      }
    }, 2500);
    return () => clearInterval(intervalRef.current);
  }, []);

  const prev = useCallback(() => {
    stopAutoPlay();
    const ci = centerImgIndexRef.current;
    const ai = activeIndexRef.current;
    if (ci > 0) {
      setCenterImgIndex(ci - 1);
    } else {
      const prevIdx = (ai - 1 + TOTAL) % TOTAL;
      setFeatureDir('right');
      setCenterImgIndex(FEATURES[prevIdx].images.length - 1);
      setActiveIndex(prevIdx);
    }
  }, [stopAutoPlay]);

  const next = useCallback(() => {
    stopAutoPlay();
    const ci = centerImgIndexRef.current;
    const ai = activeIndexRef.current;
    if (ci < FEATURES[ai].images.length - 1) {
      setCenterImgIndex(ci + 1);
    } else {
      setFeatureDir('left');
      setCenterImgIndex(0);
      setActiveIndex((ai + 1) % TOTAL);
    }
  }, [stopAutoPlay]);

  const visibleCards = [
    { feature: FEATURES[(activeIndex - 1 + TOTAL) % TOTAL], slot: 'left',   isCenter: false },
    { feature: FEATURES[activeIndex],                         slot: 'center', isCenter: true  },
    { feature: FEATURES[(activeIndex + 1) % TOTAL],          slot: 'right',  isCenter: false },
  ];

  return (
    <div className="lp-root">
      <nav className="lp-nav">
        <span className="lp-nav-logo">The English Interface</span>
        <a className="lp-nav-cta" href="#download">
          Get the App
        </a>
      </nav>

      <section className="lp-hero">
        <div className="lp-hero-bg">
          <GlitchyBackground theme="purple-theme" />
        </div>
        <div className="lp-hero-overlay" />
        <div className="lp-hero-content">
          <h1 className="lp-hero-title">The English Interface</h1>
          <p className="lp-hero-subtitle">
            Build your vocabulary.<br />
            Learn through stories and games.<br />
            Perfect your pronunciation and grammar.
          </p>
          <video
            className="lp-hero-video"
            src="https://firebasestorage.googleapis.com/v0/b/esl-videos.firebasestorage.app/o/herofolder%2Fhero-video.mp4?alt=media&token=821584c3-2c84-4e84-9f75-f972e447baec"
            controls
            autoPlay
            muted
            playsInline
            crossOrigin="anonymous"
          >
            <track
              kind="subtitles"
              src={`/captions/hero-${(navigator.language || 'en').split('-')[0]}.vtt`}
              srcLang={(navigator.language || 'en').split('-')[0]}
              default
            />
          </video>
        </div>
      </section>

      <section className="lp-features-section">
        <h2 className="lp-section-title">A complete learning tool.</h2>
        <p className="lp-section-subtitle">The best methods for learning English.</p>

        <div className="lp-carousel">
          <button className="lp-carousel-arrow lp-carousel-arrow-prev" onClick={prev} aria-label="Previous">&#8249;</button>
          <button className="lp-carousel-arrow lp-carousel-arrow-next" onClick={next} aria-label="Next">&#8250;</button>

          {/* Fixed-size container — cards are absolutely positioned inside */}
          <LayoutGroup>
            <div className="lp-carousel-track">
              <AnimatePresence initial={false}>
                {visibleCards.map(({ feature, slot, isCenter }) => (
                  <motion.div
                    key={feature.title}
                    layout
                    custom={{ dir: featureDir, isCenter }}
                    variants={cardVariants}
                    initial="enter"
                    animate="visible"
                    exit="exit"
                    transition={CARD_TRANSITION}
                    style={{
                      position: 'absolute',
                      left: SLOT[slot],
                      top: 0,
                      width: CARD_W,
                      transformOrigin: 'center center',
                    }}
                  >
                    <FeatureCard
                      title={feature.title}
                      images={feature.images}
                      imgIndex={isCenter ? centerImgIndex : featureImgRef.current[feature.title]}
                      isCenter={isCenter}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </LayoutGroup>
        </div>

        {currentDesc && <p className="lp-feature-desc">{currentDesc}</p>}

        <div className="lp-dots">
          {FEATURES.map((_, i) => (
            <span key={i} className={`lp-dot${i === activeIndex ? ' lp-dot-active' : ''}`} />
          ))}
        </div>
      </section>

      <section className="lp-cta-section" id="download">
        <h2 className="lp-cta-title">Start Learning Today</h2>
        <p className="lp-cta-sub">Download for free lessons and games.</p>
        <div className="lp-store-buttons">
          <a
            className="lp-play-btn"
            href="https://apps.apple.com/app/id6766725533"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg className="lp-play-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            <div className="lp-play-text">
              <span className="lp-play-sub">Download on the</span>
              <span className="lp-play-name">App Store</span>
            </div>
          </a>
          <a
            className="lp-play-btn"
            href="https://play.google.com/store/apps/details?id=com.mountobeans.cardapp"
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg className="lp-play-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.18 23.76c.33.18.7.24 1.06.18L16.86 12 4.24.06A1.84 1.84 0 0 0 3.18 0C2.53 0 2 .56 2 1.26v21.48c0 .7.53 1.26 1.18 1.02z" />
              <path d="M20.12 10.61l-3.06-1.76-3.46 3.15 3.46 3.15 3.09-1.78a1.51 1.51 0 0 0 0-2.76z" />
            </svg>
            <div className="lp-play-text">
              <span className="lp-play-sub">GET IT ON</span>
              <span className="lp-play-name">Google Play</span>
            </div>
          </a>
        </div>
      </section>

      <footer className="lp-footer">
        <span className="lp-footer-logo">The English Interface</span>
        <span className="lp-footer-copy">© {new Date().getFullYear()} The English Interface. All rights reserved.</span>
      </footer>
    </div>
  );
};

export default Homepage;
